import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound, Ban, CheckCircle2, ShieldCheck, LogOut } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLES = ["owner", "store_manager", "staff", "salesman", "technician"];
const LABEL = { super_admin: "Super Admin", owner: "Owner", store_manager: "Store Manager", staff: "Staff", salesman: "Salesman", technician: "Technician", seller: "Seller", customer: "Customer" };
const ROLE_CLR = { super_admin: "bg-purple-100 text-purple-700", owner: "bg-emerald-100 text-emerald-700", store_manager: "bg-blue-100 text-blue-700", staff: "bg-slate-100 text-slate-700", salesman: "bg-amber-100 text-amber-700", technician: "bg-cyan-100 text-cyan-700", seller: "bg-orange-100 text-orange-700" };

export default function AdminTeam() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "staff" });
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/staff").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createUser = async () => {
    setBusy(true);
    try { await api.post("/admin/users", form); toast.success("Naya user ban gaya"); setAddOpen(false); setForm({ name: "", email: "", phone: "", password: "", role: "staff" }); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const changeRole = async (u, role) => {
    try { await api.put(`/admin/users/${u.id}/role?role=${role}`); toast.success(`${u.email} → ${LABEL[role]}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleStatus = async (u) => {
    try { await api.put(`/admin/users/${u.id}/status`, { disabled: !u.disabled }); toast.success(u.disabled ? "Account enabled" : "Account disabled"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const forceLogout = async (u) => {
    try { await api.put(`/admin/users/${u.id}/force-logout`); toast.success(`${u.email} ke saare sessions kat gaye`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const resetPw = async () => {
    setBusy(true);
    try { await api.put(`/admin/users/${pwFor.id}/password`, { password: pw }); toast.success("Password reset ho gaya"); setPwFor(null); setPw(""); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const seller = (u) => u.role === "seller";
  const locked = (u) => u.role === "super_admin" || u.id === user?.id;

  return (
    <div className="space-y-5" data-testid="admin-team">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} className="text-emerald-600" />
          <h1 className="text-2xl font-bold">Team &amp; Roles <span className="text-slate-400 text-base">({rows.length})</span></h1>
        </div>
        <button data-testid="team-add-user" onClick={() => setAddOpen(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-sm flex items-center gap-2"><UserPlus size={16} /> Add User</button>
      </div>
      <p className="text-sm text-slate-500 -mt-2">Staff, managers aur owners ko manage karein — role assign karein, password reset karein, ya account disable karein.</p>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
            <th className="text-left py-3 px-4">Name</th><th className="text-left py-3 px-4">Email</th>
            <th className="text-left py-3 px-4">Role</th><th className="text-center py-3 px-4">Status</th>
            <th className="text-right py-3 px-4">Actions</th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid={`team-user-${u.id}`} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-2.5 px-4 font-semibold">{u.name || "—"}{u.id === user?.id && <span className="ml-1 text-[10px] text-emerald-600">(you)</span>}</td>
                <td className="py-2.5 px-4 text-slate-500">{u.email}</td>
                <td className="py-2.5 px-4">
                  {seller(u) || locked(u) ? (
                    <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${ROLE_CLR[u.role] || "bg-slate-100 text-slate-700"}`}>{LABEL[u.role] || u.role}</span>
                  ) : (
                    <select data-testid={`team-role-${u.id}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-xs font-semibold">
                      {ROLES.map((r) => <option key={r} value={r}>{LABEL[r]}</option>)}
                    </select>
                  )}
                </td>
                <td className="py-2.5 px-4 text-center">
                  {u.disabled ? <span className="text-[11px] font-bold text-rose-600 bg-rose-50 px-2 py-1 rounded-full">Disabled</span> : <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Active</span>}
                </td>
                <td className="py-2.5 px-4">
                  <div className="flex items-center justify-end gap-1.5">
                    <button data-testid={`team-reset-${u.id}`} onClick={() => { setPwFor(u); setPw(""); }} title="Reset password" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-blue-600"><KeyRound size={16} /></button>
                    {!locked(u) && (
                      <button data-testid={`team-force-logout-${u.id}`} onClick={() => forceLogout(u)} title="Force logout (sab sessions kaato)" className="p-1.5 rounded-lg text-slate-500 hover:bg-amber-50 hover:text-amber-600"><LogOut size={16} /></button>
                    )}
                    {!locked(u) && !seller(u) && (
                      u.disabled
                        ? <button data-testid={`team-enable-${u.id}`} onClick={() => toggleStatus(u)} title="Enable" className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><CheckCircle2 size={16} /></button>
                        : <button data-testid={`team-disable-${u.id}`} onClick={() => toggleStatus(u)} title="Disable" className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Ban size={16} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add user modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-sm" data-testid="team-add-modal">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-1">
            <input data-testid="team-f-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="team-f-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="team-f-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="team-f-password" type="text" placeholder="Temporary password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
            <select data-testid="team-f-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm">
              {ROLES.map((r) => <option key={r} value={r}>{LABEL[r]}</option>)}
            </select>
            <button data-testid="team-create-submit" disabled={busy} onClick={createUser} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-bold">Create User</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset password modal */}
      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent className="bg-white max-w-sm" data-testid="team-pw-modal">
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-1">
            <p className="text-sm text-slate-500">{pwFor?.email} ke liye naya password set karein.</p>
            <input data-testid="team-pw-input" type="text" placeholder="New password (min 6)" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
            <button data-testid="team-pw-submit" disabled={busy} onClick={resetPw} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-bold">Set Password</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
